import { FFmpeg } from '@ffmpeg/ffmpeg';
import { useCallback } from 'react';

import { loadFfmpeg } from '../../shared/ffmpeg/loadFfmpeg';

/** Matches studio VideoUploadModal — FFmpeg trim args expect HH:MM:SS(.mmm). */
function toTimeString(seconds: number, showMilliSeconds = true): string {
  const sec = Math.max(0, seconds);
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec - hours * 3600) / 60);
  const secs = sec - hours * 3600 - minutes * 60;

  const padded = {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: Math.floor(secs).toString().padStart(2, '0'),
  };

  const milliseconds = showMilliSeconds
    ? (secs % 1).toFixed(3).substring(1)
    : '';

  return `${padded.hours}:${padded.minutes}:${padded.seconds}${milliseconds}`;
}

// Regex pattern for parsing FFmpeg progress messages
// This is a flexible regex that handles parsing data from FFMPEG.
// It handles both N/A values and actual values, as well as Lsize variations.
// Example log lines:
// - frame= 1234 fps= 45 q=28.0 size= 1024kB time=00:00:41.33 bitrate= 203.2kbits/s speed=1.5x
// - frame= 167 fps= 16 q=-0.0 size=N/A time=00:00:02.78 bitrate=N/A speed=0.259x
// - frame= 411 fps=311 q=-0.0 Lsize=N/A time=00:00:13.70 bitrate=N/A speed=10.4x
// - frame= 337 fps= 21 q=24.0 size=    3072kB time=00:00:11.16 bitrate=2253.7kbits/s speed=0.686x
const FFMPEG_PROGRESS_REGEX =
  /frame=\s*(\d+)\s+fps=\s*([\d.]+)\s+q=([\d.-]+)\s+L?size=\s*([\w/.]+)\s+time=(\d{2}:\d{2}:\d{2}\.\d{2})\s+bitrate=\s*([\w/.]+)\s+speed=([\d.]+)x/;

// Function to parse FFmpeg progress from log messages
function parseFFmpegProgress(
  message: string,
): { progress: number; time: string } | null {
  const progressMatch = message.match(FFMPEG_PROGRESS_REGEX);

  if (progressMatch) {
    const [, frame, , , , time] = progressMatch;

    // Convert HH:MM:SS.ss to MM:SS.ss format
    const timeMatch = time.match(/(\d{2}):(\d{2}):(\d{2}\.\d{2})/);
    let cleanTime = time;
    if (timeMatch) {
      const [, hours, minutes, seconds] = timeMatch;
      const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
      cleanTime = `${totalMinutes.toString().padStart(2, '0')}:${seconds}`;
    }

    return {
      progress: parseFloat(frame),
      time: cleanTime,
    };
  }

  return null;
}

export interface VideoMetadata {
  duration: number; // Duration in seconds
  frameRate: number; // Frame rate (fps)
  totalFrames: number; // Total number of frames
  width: number; // Video width
  height: number; // Video height
}

export interface ProgressInfo {
  message?: string;
  percentage: number; // 0-100
  time: string; // "00:41.33"
}

// Import VideoEditType type
export type VideoRotation = 0 | 90 | 180 | 270;
export type VideoEditSettings = {
  trim?: [number, number];
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  mirror?: boolean;
  flip?: 0 | 90 | 180 | 270;
};

export type ImageEditSettings = {
  crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  mirror?: boolean;
  flip?: 0 | 90 | 180 | 270;
};

export default function useFfmpeg() {
  // Helper function to clean up potential leftover files
  const cleanupTempFiles = useCallback(async (ffmpeg: FFmpeg) => {
    const tempFiles = ['temp_metadata', 'input_video'];
    for (const file of tempFiles) {
      try {
        await ffmpeg.deleteFile(file);
      } catch (e) {
        // Ignore if file doesn't exist
      }
    }
  }, []);

  // Helper function to get video metadata including duration, frame rate, total frames, width, and height
  const getVideoMetadata = useCallback(
    async (
      blob: Blob,
      onProgress?: (progress: ProgressInfo) => void,
    ): Promise<VideoMetadata> => {
      const ffmpeg = await loadFfmpeg();

      // Write the blob to FFmpeg's file system
      const inputBuffer = await blob.arrayBuffer();

      // Clean up any existing temporary files first
      await cleanupTempFiles(ffmpeg);

      ffmpeg.writeFile('temp_metadata', new Uint8Array(inputBuffer));

      try {
        // Get video dimensions and rough duration estimate first
        // the estimated duration is used to calculate the initial progress percentage of
        // getting video metadata from ffmpeg. It also gives us width and height in an easy way.
        const { width, height, estimatedDuration } = await new Promise<{
          width: number;
          height: number;
          estimatedDuration: number;
        }>((resolve) => {
          const video = document.createElement('video');
          const url = URL.createObjectURL(blob);

          video.onloadedmetadata = () => {
            let duration = video.duration;

            // Handle Chrome bug with duration
            if (duration === Infinity) {
              video.currentTime = Number.MAX_SAFE_INTEGER;
              video.ontimeupdate = () => {
                video.ontimeupdate = null;
                duration = video.duration;
                video.currentTime = 0;
                URL.revokeObjectURL(url);
                resolve({
                  width: video.videoWidth,
                  height: video.videoHeight,
                  estimatedDuration: duration,
                });
              };
            } else {
              URL.revokeObjectURL(url);
              resolve({
                width: video.videoWidth,
                height: video.videoHeight,
                estimatedDuration: duration,
              });
            }
          };

          video.onerror = () => {
            URL.revokeObjectURL(url);
            resolve({ width: 0, height: 0, estimatedDuration: 0 });
          };

          video.src = url;
        });

        // Report initial progress
        if (onProgress) {
          onProgress({
            percentage: 5, // Initial setup complete
            time: '00:00',
          });
        }

        // Get duration and total frame count using FFmpeg
        let duration = estimatedDuration;
        let totalFrames = 0;
        let frameRate = 30; // Default fallback

        try {
          // Set up a log handler to capture duration and frame count
          let lastFrameCount = 0;
          let lastTime = 0;
          const metadataHandler = (event: { message: string }) => {
            const progressMatch = event.message.match(FFMPEG_PROGRESS_REGEX);

            if (progressMatch) {
              const [, frame, , , , time] = progressMatch;

              // Update frame count
              const currentFrame = parseInt(frame);
              lastFrameCount = Math.max(lastFrameCount, currentFrame);

              // Update time
              const timeMatch = time.match(/(\d{2}):(\d{2}):(\d{2}\.\d{2})/);
              if (timeMatch) {
                const [, hours, minutes, seconds] = timeMatch;
                const currentTime =
                  parseInt(hours) * 3600 +
                  parseInt(minutes) * 60 +
                  parseFloat(seconds);
                duration = Math.max(duration, currentTime);
                lastTime = currentTime;
              }
            }

            // Report progress if callback provided
            if (onProgress && estimatedDuration > 0) {
              // Use time-based progress since we have an estimated duration
              const timeProgress = Math.min(
                (lastTime / estimatedDuration) * 100,
                100,
              );

              // Scale from 5% to 95% (leaving room for final processing)
              const scaledProgress = 5 + timeProgress * 0.9;

              const minutes = Math.floor(lastTime / 60);
              const seconds = (lastTime % 60).toFixed(2);
              const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.padStart(5, '0')}`;

              onProgress({
                percentage: scaledProgress,
                time: timeString,
              });
            }
          };

          ffmpeg.on('log', metadataHandler);

          try {
            // Process the entire video to get duration and total frame count
            await ffmpeg.exec(['-i', 'temp_metadata', '-f', 'null', '-']);

            totalFrames = lastFrameCount;

            // Calculate frame rate from total frames and duration
            if (duration > 0 && totalFrames > 0) {
              frameRate = totalFrames / duration;
            }
          } finally {
            ffmpeg.off('log', metadataHandler);
          }
        } catch (error) {
          console.warn(
            'Could not get duration/frame count from FFmpeg, using fallback:',
            error,
          );

          // Use the estimated duration from video element
          duration = estimatedDuration;
          // Estimate total frames using default frame rate
          totalFrames = Math.round(duration * frameRate);
        }

        // Report final progress
        if (onProgress) {
          const minutes = Math.floor(duration / 60);
          const seconds = (duration % 60).toFixed(2);
          const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.padStart(5, '0')}`;

          onProgress({
            percentage: 100,
            time: timeString,
          });
        }

        return {
          duration,
          frameRate,
          totalFrames,
          width,
          height,
        };
      } finally {
        // Clean up
        cleanupTempFiles(ffmpeg);
      }
    },
    [cleanupTempFiles],
  );

  // Helper function to create a progress tracker
  const createProgressTracker = useCallback(
    (totalFrames: number, onProgress?: (progress: ProgressInfo) => void) => {
      return (event: { message: string }) => {
        const progress = parseFFmpegProgress(event.message);
        if (progress) {
          const percentage =
            totalFrames > 0
              ? Math.min((progress.progress / totalFrames) * 100, 100)
              : 0;
          onProgress?.({
            percentage,
            time: progress.time,
          });
        }
      };
    },
    [],
  );

  // Helper function to edit video with rotation, mirroring, cropping, and trimming.
  // preserveQuality uses CRF-based encoding at native FPS instead of the low-bitrate 30fps defaults.
  const editVideo = useCallback(
    async ({
      inputBlob,
      inputFileName,
      outputFileName,
      edits,
      onProgress = () => {},
      progressMessage = 'Editing video...',
      preserveQuality = false,
    }: {
      inputBlob: Blob;
      inputFileName: string;
      outputFileName: string;
      edits: VideoEditSettings;
      onProgress: (progress: ProgressInfo) => void;
      progressMessage?: string;
      preserveQuality?: boolean;
    }): Promise<Blob> => {
      const ffmpeg = await loadFfmpeg();

      try {
        // Write the input file to FFmpeg's file system
        const inputBuffer = await inputBlob.arrayBuffer();
        ffmpeg.writeFile(inputFileName, new Uint8Array(inputBuffer));

        // Build the filter chain based on the edits
        const filters: string[] = [];

        // Add rotation filter first if specified (must come before cropping)
        if (edits.flip) {
          switch (edits.flip) {
            case 90: // 90° clockwise
              filters.push('transpose=1');
              break;
            case 180: // 180°
              filters.push('transpose=1,transpose=1');
              break;
            case 270: // 270° clockwise (90° counter-clockwise)
              filters.push('transpose=2');
              break;
          }
        }

        // Add mirroring filter if specified (must come before cropping)
        if (edits.mirror) {
          filters.push('hflip');
        }

        // Add cropping filter if specified (must come after flipping and mirroring)
        if (edits.crop) {
          const { x, y, width, height } = edits.crop;
          filters.push(`crop=${width}:${height}:${x}:${y}`);
        }

        if (!preserveQuality && edits.trim) {
          filters.push('fps=fps=30:round=up');
        }

        // Build the FFmpeg command
        const ffmpegArgs = [];

        // Add trimming parameters if specified
        if (edits.trim) {
          const [start, end] = edits.trim;
          const duration = end - start;
          ffmpegArgs.push(
            '-ss',
            toTimeString(start),
            '-i',
            inputFileName,
            '-t',
            toTimeString(duration),
          );
        } else {
          ffmpegArgs.push('-i', inputFileName);
        }

        if (filters.length > 0) {
          ffmpegArgs.push('-vf', filters.join(','));
        }

        if (preserveQuality) {
          ffmpegArgs.push(
            '-c:v',
            'libx264',
            '-preset',
            'ultrafast',
            '-crf',
            '18',
            '-pix_fmt',
            'yuv420p',
            '-movflags',
            '+faststart',
            '-an',
            outputFileName,
          );
        } else {
          ffmpegArgs.push(
            '-c:v',
            'libx264',
            '-preset',
            'ultrafast',
            '-b:v',
            '2M',
            '-keyint_min',
            '10',
            '-g',
            '10',
            '-an',
            outputFileName,
          );
        }

        // Set up progress tracking
        const progressHandler = (event: { message: string }) => {
          const progress = parseFFmpegProgress(event.message);
          if (progress) {
            onProgress({
              percentage: Math.min(progress.progress, 100),
              time: progress.time,
              message: progressMessage,
            });
          }
        };
        ffmpeg.on('log', progressHandler);

        try {
          await ffmpeg.exec(ffmpegArgs);
        } finally {
          ffmpeg.off('log', progressHandler);
        }

        const outputData = await ffmpeg.readFile(outputFileName);

        if (typeof outputData === 'string') {
          throw Error('Invalid output data');
        }
        if (outputData.buffer instanceof SharedArrayBuffer) {
          throw new Error(
            'SharedArrayBuffer not supported for video blob creation',
          );
        }

        // Determine mime type based on output file extension or use default
        const mimeType = outputFileName.endsWith('.mp4')
          ? 'video/mp4'
          : outputFileName.endsWith('.webm')
            ? 'video/webm'
            : 'video/mp4';

        return new Blob([outputData.buffer], { type: mimeType });
      } catch (error) {
        console.error('Error editing video:', error);
        throw error;
      } finally {
        // Clean up files from ffmpeg's file system
        await ffmpeg.deleteFile(inputFileName);
        await ffmpeg.deleteFile(outputFileName);
      }
    },
    [],
  );

  // Helper function to edit image with rotation, mirroring, and cropping
  const editImage = useCallback(
    async ({
      inputBlob,
      inputFileName,
      outputFileName,
      edits,
      onProgress = () => {},
      progressMessage = 'Editing image...',
    }: {
      inputBlob: Blob;
      inputFileName: string;
      outputFileName: string;
      edits: ImageEditSettings;
      onProgress?: (progress: ProgressInfo) => void;
      progressMessage?: string;
    }): Promise<Blob> => {
      const ffmpeg = await loadFfmpeg();

      try {
        // Write the input file to FFmpeg's file system
        const inputBuffer = await inputBlob.arrayBuffer();
        ffmpeg.writeFile(inputFileName, new Uint8Array(inputBuffer));

        // Build the filter chain based on the edits
        const filters: string[] = [];

        // Add rotation filter first if specified (must come before cropping)
        if (edits.flip) {
          switch (edits.flip) {
            case 90: // 90° clockwise
              filters.push('transpose=1');
              break;
            case 180: // 180°
              filters.push('transpose=1,transpose=1');
              break;
            case 270: // 270° clockwise (90° counter-clockwise)
              filters.push('transpose=2');
              break;
          }
        }

        // Add mirroring filter if specified (must come before cropping)
        if (edits.mirror) {
          filters.push('hflip');
        }

        // Add cropping filter if specified (must come after flipping and mirroring)
        if (edits.crop) {
          const { x, y, width, height } = edits.crop;
          filters.push(`crop=${width}:${height}:${x}:${y}`);
        }

        // Build the FFmpeg command
        const ffmpegArgs = ['-i', inputFileName];

        if (filters.length > 0) {
          ffmpegArgs.push('-vf', filters.join(','));
        }

        // Add output arguments for image
        ffmpegArgs.push('-frames:v', '1', outputFileName);

        // Set up progress tracking
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const progressHandler = (_event: { message: string }) => {
          // For images, progress is typically very fast
          // Report 50% on any log event, 100% on completion
          onProgress({
            percentage: 50,
            time: '00:00',
            message: progressMessage,
          });
        };
        ffmpeg.on('log', progressHandler);

        try {
          await ffmpeg.exec(ffmpegArgs);
          // Report completion
          onProgress({
            percentage: 100,
            time: '00:00',
            message: progressMessage,
          });
        } finally {
          ffmpeg.off('log', progressHandler);
        }

        const outputData = await ffmpeg.readFile(outputFileName);

        if (typeof outputData === 'string') {
          throw Error('Invalid output data');
        }
        if (outputData.buffer instanceof SharedArrayBuffer) {
          throw new Error(
            'SharedArrayBuffer not supported for image blob creation',
          );
        }

        // Determine mime type based on output file extension or use default
        const mimeType = outputFileName.endsWith('.png')
          ? 'image/png'
          : outputFileName.endsWith('.jpg') || outputFileName.endsWith('.jpeg')
            ? 'image/jpeg'
            : outputFileName.endsWith('.webp')
              ? 'image/webp'
              : 'image/png';

        return new Blob([outputData.buffer], { type: mimeType });
      } catch (error) {
        console.error('Error editing image:', error);
        throw error;
      } finally {
        // Clean up files from ffmpeg's file system
        await ffmpeg.deleteFile(inputFileName);
        await ffmpeg.deleteFile(outputFileName);
      }
    },
    [],
  );

  return {
    loadFfmpeg,
    getVideoMetadata,
    createProgressTracker,
    editVideo,
    editImage,
  };
}
